import { IDisplayableOpenInviteDetails } from 'fitbeat.gym-core';
import moment from 'moment-timezone';
import React from 'react';
import { translate } from 'react-i18next';
import {
  ImageBackground,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import CalendarIcon from '../assets/images/calendar.svg';
import DurationIcon from '../assets/images/duration.svg';
import WelcomeBackground from '../assets/images/welcome2-background.png';
import { BookingFlowFooter } from '../Bookings';
import { Button } from '../components';
import { Icon } from '../components';
import Theme from '../theme';
import { FormError } from '../ui';
import { LinearGradient } from '../ui/components/LinearGradient';
import { getWorkoutDuration } from '../utils';
import { defaultScale, scaleImageAspectRatio } from '../utils/scaling';
import { isMobile, minDesktopWidth } from '../utils/ScreenSizes';
import { InfluencerLearnMorePartOne } from './InfluencerLearnMorePartOne';
import { InfluencerLearnMorePartThree } from './InfluencerLearnMorePartThree';
import { InfluencerLearnMorePartTwo } from './InfluencerLearnMorePartTwo';

interface IProps {
  t: (str: string, options?: { [key: string]: string }) => string;
  avatar: string | null;
  close: () => void;
  goBack: () => void;
  actionButton: () => void;
  inviteDetails: IDisplayableOpenInviteDetails;
  loading: boolean;
  error?: Error;
}

const headerSize =  Platform.OS === 'web' ?
  (isMobile() ? window.innerWidth : minDesktopWidth) : null;

const iconWidth = defaultScale(15);
const iconHeight = defaultScale(15);

class InfluencerLearnMore extends React.PureComponent<IProps> {
  public renderHeader() {
    const { t, avatar, close, inviteDetails, goBack } = this.props;
    const imageSource = avatar ? { uri: `data:image/png;base64,${avatar}` } : WelcomeBackground;
    const openedBeforeShowingSettings = !!inviteDetails.isStreamingWorkout;
    const preworkoutTalk = !!inviteDetails.livestreamOptions?.preWorkoutTalk;
    const topic = inviteDetails.livestreamOptions?.preWorkoutTalkTopic;
    return (
      <ImageBackground source={imageSource} style={[styles.header, { width: headerSize, height: headerSize }]}>
        <View>
          <SafeAreaView style={{ flex: 0 }} />
          <TouchableOpacity onPress={openedBeforeShowingSettings ? close : goBack} style={styles.closeIconContainer}>
            <Icon name='ios-arrow-back' color={Theme.PRIMARY} size={defaultScale(35)} />
          </TouchableOpacity>
        </View>
        <View style={styles.headerTextContainer}>
          <>
            <Text style={[Theme.textStyle.headingLarge, styles.headerText]}>
              {preworkoutTalk
                ? t('inviteLearnMoreHeaderWithTopic', { name: inviteDetails.referredByFullname })
                : t('joinMeForInfluencerWorkout')
              }
            </Text>

            {topic &&
              <Text style={[Theme.textStyle.heading, styles.headerText]}>
                {t('inviteLearnMoreHeaderTopic') + topic}
              </Text>
            }

            <View style={styles.iconContainer}>
              <CalendarIcon
                width={iconWidth}
                height={iconHeight}
              />
              <Text style={styles.iconText}>
                {moment(inviteDetails.workoutInviteStartTime).format('H:mm, ddd, D MMM')}
              </Text>
            </View>
            <View style={styles.iconContainer}>
              <DurationIcon
                width={iconWidth}
                height={iconHeight}
                color={Theme.WHITE}
              />
              <Text style={styles.iconText}>
                {t(
                  'inviteLearnMoreHeaderClassDuration',
                  { duration: getWorkoutDuration[inviteDetails.workoutInviteDuration] },
                )}
              </Text>
            </View>
          </>
        </View>
        <LinearGradient
          colors={[Theme.BLACK, 'transparent']}
          style={styles.gradient}
          start={{ x: 0, y: 1 }}
          end={{ x: 0, y: 0 }}
          webClassName={''}
        />
      </ImageBackground>
    );
  }

  public render() {
    const { t, loading, error, actionButton, inviteDetails } = this.props;
    const preWorkoutTalk = !!inviteDetails.livestreamOptions?.preWorkoutTalk;
    const shouldConfirmBookingOnActionPress = !!inviteDetails.livestreamOptions?.streamerWillDropOffAfterTalk;
    const showActionButton = !!inviteDetails.isStreamingWorkout;

    return (
      <View style={styles.container}>
        <ScrollView>
          {this.renderHeader()}
          {inviteDetails.referredByBio &&
            <View style={styles.section}>
              <Text style={styles.sectionHeading}>
                {t('influencerInviteLearnMoreBio', { name: inviteDetails.referredByFullname })}
              </Text>
              <Text style={Theme.textStyle.bodyCopyReg}>
                {inviteDetails.referredByBio}
              </Text>
            </View>
          }
          <View style={[styles.section, { paddingBottom: 0 }]}>
            <Text style={styles.sectionHeading}>{t('whatYouCanExpect')}</Text>
            <InfluencerLearnMorePartOne
              t={t}
              preWorkoutTalk={preWorkoutTalk}
              streamerWillDropOffAfterTalk={shouldConfirmBookingOnActionPress} />
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>
              {t('workoutChoiceQuestion')}
            </Text>
            <InfluencerLearnMorePartTwo t={t} />
          </View>
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>
              {t('bookings:whatYouNeed')}
            </Text>
            <InfluencerLearnMorePartThree t={t} />
          </View>
        </ScrollView>
        {showActionButton &&
          <BookingFlowFooter style={styles.footer}>
            <FormError
              color={Theme.CHARCOAL}
              error={error}
            />
            <Button
              fontColor={Theme.WHITE}
              isLoading={loading}
              title={
                t(shouldConfirmBookingOnActionPress
                  ? 'inviteLearnMoreBookButton' : 'inviteLearnMoreContinueButton')
              }
              onPress={actionButton}
            />
          </BookingFlowFooter>
        }
        <SafeAreaView style={{ flex: 0 }} />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  section: {
    backgroundColor: Theme.WHITE,
    padding: defaultScale(15),
    borderBottomWidth: defaultScale(10),
    borderBottomColor: Theme.GREY_FIGMA,
  },
  sectionHeading: {
    ...Theme.textStyle.heading2,
    marginBottom: defaultScale(20),
  },
  closeIconContainer: {
    alignSelf: 'flex-start',
    marginHorizontal: defaultScale(15),
  },
  header: {
    width: '100%',
    height: defaultScale(250),
    aspectRatio: scaleImageAspectRatio(0.9),
    justifyContent: 'space-between',
  },
  headerTextContainer: {
    padding: defaultScale(20),
    zIndex: 20,
  },
  headerText: {
    color: Theme.WHITE,
    zIndex: 20,
    marginTop: defaultScale(20),
  },
  gradient: {
    height: defaultScale(250),
    position: 'absolute',
    bottom : 0,
    width: '100%',
    opacity: 0.7,
  },
  iconContainer: {
    flexDirection: 'row',
    paddingVertical: defaultScale(5),
    alignItems: 'center',
  },
  iconText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.WHITE,
    marginLeft: defaultScale(6),
  },
  footer: {
    paddingHorizontal: defaultScale(25),
    paddingBottom: defaultScale(25),
  },
});

export const InfluencerLearnMorePage = translate(['auth', 'bookings'], {
  wait: true,
})(InfluencerLearnMore);
